// Pages (Lapas) — CMS content pages (about, delivery, terms, …).
//
// Manages the `cms_pages` table: slug + per-locale content (title, paragraphs,
// SEO meta) + active/position. The storefront reads these by slug via
// GET /api/v1/storefront/cms/pages/:slug.

import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FileText, Plus, Trash2, Loader2 } from "lucide-react";
import { supabase } from "@vinoga/db";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { useT } from "@/lib/i18n";

type PageRow = {
  id: string;
  slug: string;
  translations: any;
  active: boolean;
  position: number;
};

export function Pages() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const tr = useT();
  const [rows, setRows] = useState<PageRow[]>([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    const { data } = await supabase
      .from("cms_pages")
      .select("id, slug, translations, active, position")
      .order("position", { ascending: true });
    setRows((data as PageRow[]) ?? []);
    setLoading(false);
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const remove = useCallback(
    async (id: string) => {
      const { error } = await supabase.from("cms_pages").delete().eq("id", id);
      if (error) {
        toast({ title: tr("common.deleteFailed", "Delete failed"), variant: "destructive" });
        return;
      }
      setRows((prev) => prev.filter((r) => r.id !== id));
    },
    [toast, tr],
  );

  return (
    <div className="mx-auto max-w-4xl p-6">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="flex items-center gap-2 font-display text-xl font-bold text-foreground">
            <FileText size={20} className="text-primary" />
            {tr("pages.title", "Pages")}
          </h1>
          <p className="mt-1 font-body text-sm text-muted-foreground">
            {tr("pages.subtitle", "Content pages served on the storefront by slug.")}
          </p>
        </div>
        <Button size="sm" onClick={() => navigate("/pages/new")}>
          <Plus size={16} /> {tr("pages.new", "New page")}
        </Button>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 py-10 text-sm text-muted-foreground">
          <Loader2 className="animate-spin" size={16} /> {tr("common.loading", "Loading…")}
        </div>
      ) : rows.length === 0 ? (
        <p className="py-10 font-body text-sm text-muted-foreground">
          {tr("pages.empty", "No pages yet.")}
        </p>
      ) : (
        <div className="overflow-hidden rounded-lg border border-border">
          <table className="w-full text-sm">
            <thead className="border-b border-border bg-muted/30 text-left">
              <tr className="font-body text-xs uppercase tracking-wider text-muted-foreground">
                <th className="px-4 py-3">{tr("pages.colTitle", "Title")}</th>
                <th className="px-4 py-3">{tr("pages.colSlug", "Slug")}</th>
                <th className="px-4 py-3 text-center">{tr("pages.colActive", "Active")}</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr
                  key={r.id}
                  className="cursor-pointer border-b border-border last:border-0 hover:bg-muted/20"
                  onClick={() => navigate(`/pages/${r.id}`)}
                >
                  <td className="px-4 py-3 font-medium text-foreground">
                    {r.translations?.lv?.title ?? "—"}
                  </td>
                  <td className="px-4 py-3 font-mono text-xs text-muted-foreground">/{r.slug}</td>
                  <td className="px-4 py-3 text-center">
                    <span
                      className={
                        r.active
                          ? "inline-block rounded-full bg-green-100 px-2 py-0.5 text-xs text-green-700"
                          : "inline-block rounded-full bg-muted px-2 py-0.5 text-xs text-muted-foreground"
                      }
                    >
                      {r.active ? tr("pages.active", "Active") : tr("pages.inactive", "Hidden")}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right" onClick={(e) => e.stopPropagation()}>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => remove(r.id)}
                      title={tr("common.delete", "Delete")}
                    >
                      <Trash2 size={14} className="text-destructive" />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
